/**
 * Les journaux d'un exercice : ce que chacun porte, et si sa numérotation
 * tient.
 *
 * ## Ce que la page dit
 *
 * Une ligne par journal du plan, même vide : un journal sans écriture le dit,
 * il ne disparaît pas. Pour chacun, les écritures validées, les brouillons,
 * les extournes, les mouvements au débit et au crédit des validées — égaux par
 * construction — et la numérotation, qui doit aller de 1 au dernier numéro
 * sans trou. Chaque journal mène à ses écritures.
 *
 * ## Ce qu'elle ne fait pas
 *
 * Aucun geste. Le journal des ventes se relance sur accord explicite, pas
 * depuis cet écran ; les journaux de trésorerie (ING, caisse, Stripe) restent
 * vides tant que leurs faits sources ne sont pas comptabilisés (lot 3).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, BookOpen, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import clsx from 'clsx';
import { messageErreur } from '../../../lib/payments/erreur';
import {
  lireLesExercices, lireLesJournaux, type ExerciceTenu,
} from '../../../lib/compta/ecritures/gateway.supabase';
import {
  exerciceParDefaut, lienVersLesEcritures, totalDesJournaux, TYPES_DE_JOURNAL,
  type JournalDeLExercice,
} from '../../../lib/compta/ecritures/types';
import { dateCourte, euros } from '../components/money';

const STATUTS_EXERCICE: Record<string, string> = {
  ouvert: 'ouvert',
  en_cloture: 'en clôture',
  cloture: 'clôturé',
};

export default function JournauxPage() {
  const [parametres, setParametres] = useSearchParams();
  const [exercices, setExercices] = useState<ExerciceTenu[]>([]);
  const [exercice, setExercice] = useState<number | null>(() => {
    const a = parametres.get('exercice');
    return a && /^\d{4}$/.test(a) ? Number(a) : null;
  });
  const [journaux, setJournaux] = useState<JournalDeLExercice[]>([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  /* Les exercices, lus une fois ; à défaut d'un exercice dans l'adresse, le plus récent qui n'est pas clôturé. */
  useEffect(() => {
    let vivant = true;
    lireLesExercices()
      .then((ex) => {
        if (!vivant) return;
        setExercices(ex);
        setExercice((courant) => courant ?? exerciceParDefaut(ex));
        if (ex.length === 0) setChargement(false);
      })
      .catch((e) => { if (vivant) { setErreur(messageErreur(e)); setChargement(false); } });
    return () => { vivant = false; };
  }, []);

  const charger = useCallback(async () => {
    if (exercice === null) return;
    setChargement(true);
    setErreur(null);
    try {
      setJournaux(await lireLesJournaux(exercice));
    } catch (e) {
      setErreur(messageErreur(e, 'Journaux illisibles'));
    } finally {
      setChargement(false);
    }
  }, [exercice]);

  useEffect(() => { void charger(); }, [charger]);

  const changerExercice = (annee: number) => {
    setExercice(annee);
    setParametres({ exercice: String(annee) }, { replace: true });
  };

  const total = useMemo(() => totalDesJournaux(journaux), [journaux]);
  const statut = exercices.find((e) => e.annee === exercice)?.statut;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <BookOpen className="h-6 w-6 text-primary-600" /> Journaux
          </h1>
          <p className="max-w-3xl text-gray-600">
            Ce que chaque journal porte pour l’exercice, et sa numérotation — continue, sans trou, par
            journal et par exercice. Lecture seule : rien ne se comptabilise d’ici.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {exercices.length > 0 && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              Exercice
              <select className="form-input w-auto py-2" value={exercice ?? ''}
                      onChange={(e) => changerExercice(Number(e.target.value))}>
                {exercices.map((e) => (
                  <option key={e.annee} value={e.annee}>
                    {e.annee} — {STATUTS_EXERCICE[e.statut] ?? e.statut}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button type="button" onClick={charger} disabled={chargement || exercice === null}
                  className="btn-outline flex items-center">
            <RefreshCw className={clsx('mr-2 h-4 w-4', chargement && 'animate-spin')} />
            Actualiser
          </button>
        </div>
      </div>

      {erreur ? (
        <div className="rounded-lg bg-red-50 p-4">
          <p className="flex items-start gap-2 text-red-700">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" /> {erreur}
          </p>
        </div>
      ) : chargement ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      ) : exercice === null ? (
        <div className="rounded-xl bg-white p-8 text-center shadow-md">
          <p className="text-gray-600">Aucun exercice n’est ouvert dans la comptabilité.</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-600">
            Exercice <strong>{exercice}</strong>{statut ? ` (${STATUTS_EXERCICE[statut] ?? statut})` : ''} :{' '}
            <strong className="tabular-nums">{total.nbValidees.toLocaleString('fr-BE')}</strong> écriture
            {total.nbValidees > 1 ? 's' : ''} validée{total.nbValidees > 1 ? 's' : ''},{' '}
            <strong className="tabular-nums">{euros(total.totalDebitCents)}</strong> au débit et{' '}
            <strong className="tabular-nums">{euros(total.totalCreditCents)}</strong> au crédit
            {total.nbBrouillons > 0 && (
              <>, et <strong className="tabular-nums text-amber-800">{total.nbBrouillons}</strong> brouillon{total.nbBrouillons > 1 ? 's' : ''}</>
            )}
            .{' '}
            {total.continus
              ? 'Tous les journaux sont numérotés sans trou.'
              : <span className="font-medium text-red-700">Au moins un journal a une numérotation interrompue.</span>}
          </p>

          <div className="overflow-hidden rounded-xl bg-white shadow-md">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3">Journal</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3 text-right">Validées</th>
                    <th className="px-4 py-3 text-right">Brouillons</th>
                    <th className="px-4 py-3 text-right">Extournes</th>
                    <th className="px-4 py-3 text-right">Débit</th>
                    <th className="px-4 py-3 text-right">Crédit</th>
                    <th className="px-4 py-3">Numéros</th>
                    <th className="px-4 py-3">Période couverte</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {journaux.map((j) => {
                    const vide = j.nbValidees === 0 && j.nbBrouillons === 0;
                    return (
                      <tr key={j.code} className={clsx(!j.actif && 'bg-gray-50 text-gray-400')}>
                        <td className="px-4 py-3">
                          {vide ? (
                            <span className="font-medium">{j.code}</span>
                          ) : (
                            <Link to={lienVersLesEcritures({ journal: j.code, exercice: j.exercice })}
                                  className="font-medium text-primary-700 hover:underline">
                              {j.code}
                            </Link>
                          )}
                          <span className="ml-2 text-gray-600">{j.libelle}</span>
                          {!j.actif && <span className="ml-2 text-xs">(inactif)</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {TYPES_DE_JOURNAL[j.type] ?? j.type}
                          {j.compteTresorerie && <span className="ml-1 text-xs text-gray-500">({j.compteTresorerie})</span>}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{j.nbValidees.toLocaleString('fr-BE')}</td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {j.nbBrouillons > 0 ? (
                            <Link to={lienVersLesEcritures({ journal: j.code, exercice: j.exercice, statut: 'brouillon' })}
                                  className="font-medium text-amber-800 hover:underline">
                              {j.nbBrouillons}
                            </Link>
                          ) : '0'}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{j.nbExtournes}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{euros(j.totalDebitCents)}</td>
                        <td className={clsx('px-4 py-3 text-right tabular-nums',
                          j.totalDebitCents !== j.totalCreditCents && 'font-medium text-red-700')}>
                          {euros(j.totalCreditCents)}
                        </td>
                        <td className="px-4 py-3">
                          {j.dernierNumero === null ? (
                            <span className="text-gray-400">—</span>
                          ) : (
                            <span className="tabular-nums">
                              {j.premierNumero} → {j.dernierNumero}
                            </span>
                          )}
                          {j.numerotationContinue ? (
                            j.dernierNumero !== null && (
                              <CheckCircle2 className="ml-2 inline h-4 w-4 text-emerald-600" aria-label="sans trou" />
                            )
                          ) : (
                            <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800"
                                  title={`${j.nbValidees} écritures validées, dernier numéro ${j.dernierNumero ?? '—'}, compteur ${j.compteur ?? '—'}`}>
                              numérotation interrompue
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                          {j.premiereDate ? `${dateCourte(j.premiereDate)} → ${dateCourte(j.derniereDate)}` : (
                            <span className="text-gray-400" title="aucune écriture validée">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {journaux.some((j) => j.type === 'financier')
            && journaux.filter((j) => j.type === 'financier').every((j) => j.nbValidees + j.nbBrouillons === 0) && (
            <p className="text-xs text-gray-500">
              Les journaux financiers sont vides : les encaissements, remboursements et versements ne sont
              pas encore comptabilisés (lot 3).
            </p>
          )}
        </>
      )}
    </div>
  );
}
